
import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { X, Filter, SortAsc, SortDesc } from 'lucide-react';

export interface FilterState {
  category?: string;
  contentType?: string;
  sortBy?: 'date-desc' | 'date-asc' | 'title-asc' | 'title-desc' | 'views-desc';
  search?: string;
}

interface TravelByteFiltersProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  categories: string[];
  contentTypes: { value: string; label: string; }[];
}

export function TravelByteFilters({ 
  filters, 
  onFiltersChange, 
  categories, 
  contentTypes 
}: TravelByteFiltersProps) {
  const handleFilterChange = (key: keyof FilterState, value: string | undefined) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const clearAllFilters = () => {
    onFiltersChange({
      sortBy: filters.sortBy // Keep sorting
    });
  };

  const hasActiveFilters = filters.category || filters.contentType || filters.search;

  const getSortLabel = (sortBy?: string) => {
    switch (sortBy) {
      case 'date-desc': return 'Newest First';
      case 'date-asc': return 'Oldest First';
      case 'title-asc': return 'Title A-Z';
      case 'title-desc': return 'Title Z-A';
      case 'views-desc': return 'Most Popular';
      default: return 'Sort By';
    }
  };

  const getSortIcon = (sortBy?: string) => {
    if (sortBy?.includes('desc')) return <SortDesc className="h-4 w-4" />;
    if (sortBy?.includes('asc')) return <SortAsc className="h-4 w-4" />;
    return <Filter className="h-4 w-4" />;
  };

  return (
    <div className="space-y-4">
      {/* Filter Controls - Mobile Optimized Layout */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-3">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Filters:</span>
        </div>
        
        {/* Filters Container - Responsive Grid Layout with Touch-Optimized Spacing */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-3 w-full sm:w-auto">
          {/* Category Filter */}
          <Select 
            value={filters.category || "all-categories"} 
            onValueChange={(value) => handleFilterChange('category', value === "all-categories" ? undefined : value)}
          >
            <SelectTrigger className="w-full min-w-[140px] h-11 sm:h-10 px-4 py-3 sm:py-2 touch-manipulation">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all-categories">All Categories</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category} value={category}>
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Content Type Filter */}
          <Select 
            value={filters.contentType || "all-types"} 
            onValueChange={(value) => handleFilterChange('contentType', value === "all-types" ? undefined : value)}
          >
            <SelectTrigger className="w-full min-w-[140px] h-11 sm:h-10 px-4 py-3 sm:py-2 touch-manipulation">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all-types">All Types</SelectItem>
              {contentTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Sort By */}
          <Select 
            value={filters.sortBy || "date-desc"} 
            onValueChange={(value) => handleFilterChange('sortBy', value as any)}
          >
            <SelectTrigger className="w-full min-w-[160px] h-11 sm:h-10 px-4 py-3 sm:py-2 touch-manipulation">
              <div className="flex items-center gap-2">
                {getSortIcon(filters.sortBy)}
                <SelectValue placeholder="Sort By" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date-desc">Newest First</SelectItem>
              <SelectItem value="date-asc">Oldest First</SelectItem>
              <SelectItem value="title-asc">Title A-Z</SelectItem>
              <SelectItem value="title-desc">Title Z-A</SelectItem>
              <SelectItem value="views-desc">Most Popular</SelectItem>
            </SelectContent>
          </Select>

          {/* Clear Filters - Touch-Optimized */}
          {hasActiveFilters && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={clearAllFilters}
              className="w-full sm:w-auto h-11 sm:h-9 px-4 py-3 sm:py-2 justify-center sm:justify-start touch-manipulation"
            >
              <X className="h-3 w-3 mr-1" />
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Active Filters Display - Touch-Optimized */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-3 sm:gap-2">
          <span className="text-xs text-muted-foreground">Active filters:</span>
          
          {filters.search && (
            <Badge variant="secondary" className="text-xs py-1.5 sm:py-1 px-3 sm:px-2">
              Search: "{filters.search}"
              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0 ml-2 sm:ml-1 hover:bg-transparent touch-manipulation"
                onClick={() => handleFilterChange('search', undefined)}
              >
                <X className="h-3 w-3 sm:h-2 sm:w-2" />
              </Button>
            </Badge>
          )}
          
          {filters.category && (
            <Badge variant="secondary" className="text-xs py-1.5 sm:py-1 px-3 sm:px-2">
              Category: {filters.category}
              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0 ml-2 sm:ml-1 hover:bg-transparent touch-manipulation"
                onClick={() => handleFilterChange('category', undefined)}
              >
                <X className="h-3 w-3 sm:h-2 sm:w-2" />
              </Button>
            </Badge>
          )}
          
          {filters.contentType && (
            <Badge variant="secondary" className="text-xs py-1.5 sm:py-1 px-3 sm:px-2">
              Type: {contentTypes.find(t => t.value === filters.contentType)?.label || filters.contentType}
              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0 ml-2 sm:ml-1 hover:bg-transparent touch-manipulation"
                onClick={() => handleFilterChange('contentType', undefined)}
              >
                <X className="h-3 w-3 sm:h-2 sm:w-2" />
              </Button>
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}
